/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '~environments/environment';
import {
  AccessToken,
  AuthResponse,
  Otp,
  SocialNetworkName,
  UserCreationParams
} from '~core/models/auth.models';
import { addUserVerifiedProperties } from '~backoffice/app/~shared/utils/auth';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { Observable, map, tap } from 'rxjs';
import {
  SKIP_ERROR_INTERCEPTOR,
  SKIP_JWT_INTERCEPTOR
} from '~backoffice/app/~shared/constants/global.constants';

import { ApiConstantsService } from '~core/services/api-constants.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private _httpClient = inject(HttpClient);
  private _globalFacade = inject(GlobalFacade);
  private _toastService = inject(ToastService);
  private _apiConstantsService = inject(ApiConstantsService);

  private _lastUsedToken: string | undefined;

  get lastUsedToken(): string | undefined {
    return this._lastUsedToken;
  }

  set lastUsedToken(v: string | undefined) {
    this._lastUsedToken = v;
  }

  loginWithPassword(params: {
    data: {
      username: string;
      password: string;
    };
    redirectUrl?: string;
  }): Observable<AuthResponse> {
    const { data } = params;
    const options = {
      identity: {
        methods: ['password'],
        password: {
          user: {
            username: data.username,
            password: data.password
          }
        }
      }
    };

    return this._httpClient
      .post<AuthResponse>(this._apiConstantsService.tokensUrl, options, {
        context: new HttpContext()
          .set(SKIP_JWT_INTERCEPTOR, true)
          .set(SKIP_ERROR_INTERCEPTOR, true)
      })
      .pipe(
        map(response => ({
          ...response,
          user: addUserVerifiedProperties(response.user)
        }))
      );
  }

  loginWithOrganizationNtd(params: {
    data: { username: string; password: string };
    organizationNtd: string;
    redirectUrl?: string;
  }): Observable<AuthResponse> {
    const { data, organizationNtd } = params;
    const identity = {
      methods: ['password'],
      password: {
        user: {
          username: data.username,
          password: data.password
        }
      }
    };

    const options = { identity };
    const headers = new HttpHeaders({
      'X-Organization-Ntd': organizationNtd
    });

    return this._httpClient
      .post<AuthResponse>(this._apiConstantsService.tokensUrl, options, {
        context: new HttpContext().set(SKIP_JWT_INTERCEPTOR, true),
        headers
      })
      .pipe(
        map(response => ({
          ...response,
          user: addUserVerifiedProperties(response.user)
        }))
      );
  }

  registerWithPasswordAndOrganization(
    params: UserCreationParams
  ): Observable<AuthResponse> {
    const options = {
      identity: {
        methods: ['password'],
        password: {
          user: {
            username: params.email,
            password: params.password
          }
        }
      },
      organization: {
        ...params.organization
      },
      useStateFormViolationHandler: true
    };

    return this._httpClient
      .post<AuthResponse>(this._apiConstantsService.tokensUrl, options, {
        context: new HttpContext().set(SKIP_JWT_INTERCEPTOR, true)
      })
      .pipe(
        map(response => ({
          ...response,
          user: addUserVerifiedProperties(response.user)
        }))
      );
  }

  loginWithOneTimePassword(params: {
    data: {
      username?: string;
      otpCode?: string;
    };
    redirectUrl?: string;
  }): Observable<AuthResponse> {
    const { data } = params;
    const options = {
      identity: {
        methods: ['otp'],
        otp: {
          user: {
            username: data.username,
            code: data.otpCode
          }
        }
      }
    };

    return this._httpClient
      .post<AuthResponse>(this._apiConstantsService.tokensUrl, options, {
        context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
      })
      .pipe(
        map(response => ({
          ...response,
          user: addUserVerifiedProperties(response.user)
        }))
      );
  }

  loginWithAccessToken(accessTokenId: string): Observable<AuthResponse> {
    const options = {
      identity: {
        methods: ['accessToken'],
        accessToken: {
          token: {
            id: accessTokenId
          }
        }
      }
    };

    const context = new HttpContext()
      .set(SKIP_JWT_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);

    return this._httpClient.post<AuthResponse>(
      this._apiConstantsService.tokensUrl,
      options,
      {
        context
      }
    );
  }

  loginWithSocialNetwork(params: {
    method: SocialNetworkName;
    tokenId: string;
  }): Observable<AuthResponse> {
    const { method, tokenId } = params;
    const options = {
      identity: {
        methods: [method],
        [method]: {
          token: {
            id: tokenId
          }
        }
      }
    };

    return this._httpClient
      .post<AuthResponse>(this._apiConstantsService.tokensUrl, options, {
        context: new HttpContext().set(SKIP_JWT_INTERCEPTOR, true)
      })
      .pipe(
        map(response => ({
          ...response,
          user: addUserVerifiedProperties(response.user)
        }))
      );
  }

  refreshToken(refreshTokenId: string): Observable<AuthResponse> {
    const options = {
      identity: {
        methods: ['refreshToken'],
        refreshToken: {
          token: {
            id: refreshTokenId
          }
        }
      }
    };

    const context = new HttpContext()
      .set(SKIP_JWT_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);

    return this._httpClient.post<AuthResponse>(
      this._apiConstantsService.tokensUrl,
      options,
      {
        context
      }
    );
  }

  createOtp(options: {
    field: 'email' | 'phoneNumber';
    value: string;
  }): Observable<Otp> {
    return this._httpClient.post<Otp>(
      this._apiConstantsService.otpUrl,
      options
    );
  }

  getResetOtp(options: {
    field: 'email' | 'phoneNumber';
    value: string;
  }): Observable<Otp> {
    return this._httpClient
      .post<Otp>(
        `${this._apiConstantsService.usersUrl}/password/resetting/request`,
        {
          username: options.value,
          resetUrl: `${environment.redirectUrl}/auth/forgot-password`,
          context: {
            channel: options.field,
            platform: 'default'
          }
        },
        {
          context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
        }
      )
      .pipe(
        tap({
          error: error => {
            if (error.status === 404) {
              this._toastService.open({
                content: `Ce compte n'existe pas ou à été supprimé`,
                type: 'warning'
              });
            }
          }
        })
      );
  }

  resetPassword(email: string): Observable<Otp> {
    return this._httpClient
      .post<Otp>(
        `${this._apiConstantsService.usersUrl}/password/resetting/request`,
        {
          username: email,
          resetUrl: `${environment.redirectUrl}/password/resetting/request`
        }
      )
      .pipe(
        tap({
          error: error => this._globalFacade.handleFormViolations(error)
        })
      );
  }

  resetPasswordWithToken(password: string, token: string): Observable<unknown> {
    return this._httpClient
      .post(
        `${this._apiConstantsService.usersUrl}/password/resetting/reset`,
        {
          password
        },
        {
          context: new HttpContext().set(SKIP_JWT_INTERCEPTOR, true),
          headers: {
            authorization: `Otp ${token}`,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'content-type': 'application/json'
          }
        }
      )
      .pipe(
        tap({
          error: error => this._globalFacade.handleFormViolations(error)
        })
      );
  }

  changePassword(options: {
    userId: string;
    password: string;
    newPassword: string;
  }): Observable<unknown> {
    const { userId, password, newPassword } = options;
    return this._httpClient
      .put(`${this._apiConstantsService.usersUrl}/${userId}/change/password`, {
        password,
        newPassword
      })
      .pipe(
        tap({
          error: error => this._globalFacade.handleFormViolations(error)
        })
      );
  }

  logout(accessToken: AccessToken): Observable<unknown> {
    return this._httpClient.get(this._apiConstantsService.logoutUrl, {
      context: new HttpContext()
        .set(SKIP_JWT_INTERCEPTOR, true)
        .set(SKIP_ERROR_INTERCEPTOR, true),
      headers: { authorization: `Bearer ${accessToken}` }
    });
  }

  // Social Auth

  getGoogleOauthToken(isRegistration?: boolean): void {
    const clientId = environment.googleOauthClientId;
    const redirectUrl = environment.googleOauthRedirectUrl;
    const state = encodeURIComponent(
      JSON.stringify({ appName: SocialNetworkName.google, isRegistration })
    );
    const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?&include_granted_scopes=true&response_type=token&redirect_uri=${redirectUrl}&scope=email%20profile&state=${state}&client_id=${clientId}`;

    window.open(oauthUrl, '_self');
  }

  getFacebookOauthToken(isRegistration?: boolean): void {
    const clientId = environment.facebookOauthAppId;
    const redirectUrl = environment.facebookOauthRedirectUrl;
    const state = encodeURIComponent(
      JSON.stringify({ appName: SocialNetworkName.facebook, isRegistration })
    );

    const oauthUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${clientId}&response_type=token&granted_scopes=email,public_profiles&state=${state}&redirect_uri=${redirectUrl}`;

    window.open(oauthUrl, '_self');
  }

  getAppleOauthToken(isRegistration?: boolean): void {
    const clientId = environment.appleOauthClientId;
    const redirectUrl = environment.appleOauthRedirectUrl;
    const state = encodeURIComponent(
      JSON.stringify({ appName: SocialNetworkName.apple, isRegistration })
    );

    const oauthUrl = `https://appleid.apple.com/auth/authorize?client_id=${clientId}&response_type=code%20id_token&response_mode=fragment&state=${state}&redirect_uri=${redirectUrl}`;

    window.open(oauthUrl, '_self');
  }
}
